import React from 'react';
import SimpleDropdownPopup from '../ui/SimpleDropdownPopup';
import { ChangeIndicator } from '../common/ChangeIndicator';

interface Barcode {
  id: string;
  gtin: string;
  unitDescriptor: string;
  isMainBarcode: boolean;
}

interface BarcodesTableProps {
  data: any;
  mode: 'view' | 'edit';
  onChange: (field: string, value: any) => void;
  isSupplierMode?: boolean;
  originalData?: any;
  articleNumber?: string;
}

export const BarcodesTable: React.FC<BarcodesTableProps> = ({ 
  data, 
  mode,
  onChange, 
  isSupplierMode = false,
  originalData,
  articleNumber
}) => {
  // **UTILITY FUNCTIONS FOR CHANGE DETECTION**
  const getFieldValue = (obj: any, path: string) => {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  };

  const isFieldChanged = (fieldPath: string): boolean => {
    if (!originalData || !data) return false;
    
    // Use submissionSnapshot as baseline if available, otherwise use originalData
    const baselineData = data?.submissionSnapshot || originalData;
    
    // Special handling for barcodes array
    if (fieldPath === 'barcodes') {
      const originalBarcodes = getFieldValue(baselineData, fieldPath) || [];
      const currentBarcodes = getFieldValue(data, fieldPath) || [];
      
      if (originalBarcodes.length !== currentBarcodes.length) return true;
      return originalBarcodes.some((item: any, index: number) => 
        JSON.stringify(item) !== JSON.stringify(currentBarcodes[index])
      );
    }
    
    // Special handling for individual barcode field changes (e.g., 'barcodes[0].gtin')
    if (fieldPath.match(/^barcodes\[\d+\]\./)) {
      const currentBarcodes = getFieldValue(data, 'barcodes') || [];
      const baselineBarcodes = getFieldValue(baselineData, 'barcodes') || [];
      
      // Extract barcode index and field from fieldPath like 'barcodes[0].gtin'
      const match = fieldPath.match(/^barcodes\[(\d+)\]\.(.+)$/);
      if (match) {
        const barcodeIndex = parseInt(match[1]);
        const fieldName = match[2];
        const currentBarcode = currentBarcodes[barcodeIndex];
        const baselineBarcode = baselineBarcodes[barcodeIndex];
        
        // Compare the specific field values
        const currentValue = currentBarcode?.[fieldName];
        const baselineValue = baselineBarcode?.[fieldName];
        
        return String(baselineValue || '') !== String(currentValue || '');
      }
    }
    
    const originalValue = getFieldValue(baselineData, fieldPath);
    const currentValue = getFieldValue(data, fieldPath);
    
    // Handle null/undefined cases
    if (!originalValue && !currentValue) return false;
    if (!originalValue && currentValue) return true;
    if (originalValue && !currentValue) return true;
    
    // For arrays
    if (Array.isArray(originalValue) && Array.isArray(currentValue)) {
      if (originalValue.length !== currentValue.length) return true;
      return originalValue.some((item: any, index: number) => 
        JSON.stringify(item) !== JSON.stringify(currentValue[index])
      );
    }
    
    // For objects
    if (typeof originalValue === 'object' && typeof currentValue === 'object') {
      return JSON.stringify(originalValue) !== JSON.stringify(currentValue);
    }
    
    // For primitive values
    return originalValue !== currentValue;
  };

  const isFieldNew = (fieldPath: string): boolean => {
    if (!originalData || !data) return false;
    
    // IMPORTANT: If a submissionSnapshot exists, the field can NEVER be "new" - only "changed"
    // submissionSnapshot means the supplier has already submitted data before
    const hasSubmissionSnapshot = data?.submissionSnapshot;
    if (hasSubmissionSnapshot) {
      return false; // Never "new" if already submitted before
    }
    
    // Special handling for barcodes array
    if (fieldPath === 'barcodes') {
      const originalBarcodes = getFieldValue(originalData, fieldPath) || [];
      const currentBarcodes = getFieldValue(data, fieldPath) || [];
      
      return originalBarcodes.length === 0 && currentBarcodes.length > 0;
    }
    
    // Special handling for individual barcode field changes (e.g., 'barcodes[0].gtin')
    if (fieldPath.match(/^barcodes\[\d+\]\./)) {
      const currentBarcodes = getFieldValue(data, 'barcodes') || [];
      const originalBarcodes = getFieldValue(originalData, 'barcodes') || [];
      
      // Extract barcode index and field from fieldPath like 'barcodes[0].gtin'
      const match = fieldPath.match(/^barcodes\[(\d+)\]\.(.+)$/);
      if (match) {
        const barcodeIndex = parseInt(match[1]);
        const fieldName = match[2];
        const currentBarcode = currentBarcodes[barcodeIndex];
        const originalBarcode = originalBarcodes[barcodeIndex];
        
        // Field is "new" if original was empty/null and current has a value
        const originalValue = originalBarcode?.[fieldName];
        const currentValue = currentBarcode?.[fieldName];
        
        const originalEmpty = !originalValue || originalValue === '' || originalValue === null;
        const currentHasValue = currentValue && currentValue !== '' && currentValue !== null;
        
        return originalEmpty && currentHasValue;
      }
    }
    
    const originalValue = getFieldValue(originalData, fieldPath);
    const currentValue = getFieldValue(data, fieldPath);
    
    // Field is new if original was empty/null and current has a value
    const originalEmpty = !originalValue || originalValue === '' || originalValue === null;
    const currentHasValue = currentValue && currentValue !== '' && currentValue !== null;
    
    return originalEmpty && currentHasValue;
  };

  // **ENHANCED UTILITY FUNCTION FOR RENDERING FIELDS WITH CHANGE HIGHLIGHTING**
  const renderFieldWithHistory = (
    fieldName: string, 
    fieldPath: string,
    content: React.ReactNode
  ) => {
    // Debug-Ausgaben
    console.log(`[BARCODES_RENDER_FIELD_WITH_HISTORY] ${fieldName}:`, {
      isSupplierMode,
      hasOriginalData: !!originalData,
      hasArticleNumber: !!articleNumber,
      fieldPath,
      mode
    });
    
    // Change tracking only for Admin (not Supplier mode) AND only in view mode
    if (isSupplierMode || !originalData || !articleNumber || mode === 'edit') {
      console.log(`[BARCODES_RENDER_FIELD_WITH_HISTORY] ${fieldName}: Returning plain content (isSupplierMode: ${isSupplierMode}, mode: ${mode})`);
      return content;
    }
    
    const hasChanges = isFieldChanged(fieldPath);
    const isNew = isFieldNew(fieldPath);
    
    console.log(`[BARCODES_CHANGE_INDICATOR] ${fieldName}:`, {
      hasChanges,
      isNew,
      fieldPath,
      articleNumber,
      mode
    });
    
    return (
      <ChangeIndicator
        fieldName={fieldName}
        fieldPath={fieldPath}
        hasChanges={hasChanges}
        isNew={isNew}
        articleNumber={articleNumber}
        className="overflow-visible"
        positionOutside={true} // Barcodes sollen außerhalb sein
      >
        {content}
      </ChangeIndicator>
    );
  };
  // Initialisiere 2 leere Barcodes wenn keine vorhanden sind
  React.useEffect(() => {
    if (!data?.barcodes || data.barcodes.length === 0) {
      const initialBarcodes = Array.from({ length: 2 }, (_, index) => ({
        id: `initial-${index}`,
        gtin: '',
        unitDescriptor: '',
        isMainBarcode: false
      }));
      onChange('barcodes', initialBarcodes);
    }
  }, []);

  const addNewBarcode = () => {
    const newBarcode: Barcode = {
      id: Date.now().toString(),
      gtin: '',
      unitDescriptor: '',
      isMainBarcode: false
    };
    const currentBarcodes = data?.barcodes || [];
    onChange('barcodes', [...currentBarcodes, newBarcode]);
  };

  const updateBarcode = (index: number, updatedBarcode: Partial<Barcode>) => {
    const updatedBarcodes = [...(data?.barcodes || [])];
    updatedBarcodes[index] = { ...updatedBarcodes[index], ...updatedBarcode };
    onChange('barcodes', updatedBarcodes);
  };

  const deleteBarcode = (index: number) => {
    const updatedBarcodes = data?.barcodes?.filter((_: any, i: number) => i !== index) || [];
    onChange('barcodes', updatedBarcodes);
  };

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header */}
      <div className="flex justify-between items-center px-4 py-3 border-b border-gray-200 bg-gray-50 rounded-t-lg">
        <h3 className="text-base font-semibold text-gray-900">Barcodes</h3>
        <button
          type="button"
          onClick={addNewBarcode}
          className="bg-blue-600 text-white px-3 py-1.5 rounded-md hover:bg-blue-700 transition-colors text-sm font-medium"
        >
          + Hinzufügen
        </button>
      </div>
      
      {/* Table Container */}
      <div className="flex-1 p-3">
        <div className="w-full border border-gray-300 rounded-lg overflow-hidden">
          <table className="w-full border-collapse bg-white relative">
            <thead className="bg-gray-50">
              <tr className="border-b border-gray-200">
                <th className="w-[50%] px-2 py-2 text-left text-xs font-medium text-gray-700">
                  GTIN/Barcode
                </th>
                <th className="w-[35%] px-2 py-2 text-left text-xs font-medium text-gray-700">
                  Einheit
                </th>
                <th className="w-[15%] px-2 py-2 text-center text-xs font-medium text-gray-700">
                  Aktionen
                </th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {data?.barcodes && data.barcodes.length > 0 ? (
                data.barcodes.map((barcode: Barcode, index: number) => (
                  <tr key={barcode.id || index} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                    {/* GTIN/Barcode */}
                    <td className="px-2 py-1.5">
                      {renderFieldWithHistory(
                        `Barcode ${index + 1} - GTIN`,
                        `barcodes[${index}].gtin`,
                        (
                          <input
                            type="text"
                            value={barcode.gtin || ''}
                            onChange={(e) => updateBarcode(index, { gtin: e.target.value })}
                            className="w-full h-8 border border-gray-300 rounded px-2 text-xs font-mono focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="GTIN/Barcode..."
                          />
                        )
                      )}
                    </td>
                    
                    {/* Einheit */}
                    <td className="px-2 py-1.5">
                      <div className="w-full">
                        {renderFieldWithHistory(
                          `Barcode ${index + 1} - Einheit`,
                          `barcodes[${index}].unitDescriptor`,
                          (
                            <SimpleDropdownPopup
                              category="unitDescriptor"
                              value={barcode.unitDescriptor || ''}
                              onChange={(value: string) => updateBarcode(index, { unitDescriptor: value })}
                              label=""
                              placeholder="Einheit..."
                              allowAdminMode={!isSupplierMode}
                            />
                          )
                        )}
                      </div>
                    </td>
                    
                    {/* Aktionen */}
                    <td className="px-2 py-1.5 text-center">
                      <button
                        type="button"
                        onClick={() => deleteBarcode(index)}
                        className="inline-flex items-center justify-center w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full transition-colors duration-200"
                        title="Barcode löschen"
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={3} className="px-6 py-8 text-center text-gray-500">
                    <div className="flex flex-col items-center space-y-2">
                      <svg className="w-8 h-8 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h4" />
                      </svg>
                      <p className="text-sm font-medium">Keine Barcodes definiert</p>
                      <p className="text-xs">Klicken Sie auf "Hinzufügen" um zu beginnen</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
